import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import Select from "react-select";
import * as Yup from "yup";
import DatePicker from "react-multi-date-picker";
import { useLocation } from "react-router-dom";

import FormInputLayout from "../FormInputLayout";
import { colourStylesFullWidth } from "../../data/Constants";
import PlainButton from "../buttons/PlainButton";
import moment from "moment";

const CPD_SUBMISSION_STATUS_CONSTANT = {
    PENDING: 1,
    SUBMITTED: 2,
    APPROVED: 3,
    REJECTED: 4,
    KIV: 5,
    ALL: 6,
};

const rejectionReasons = {
    REJECTED: [
        "Sorry, your application has been rejected. The CPD application should cover the related Technology and Technical area and not limited to the field registered and existing programs approved by MBOT.",
        "Sorry, your application has been rejected. Please be informed, activities as you state above are not considered as CPD Hours. The CPD activities must be relevant to the enhancement and competency of members.",
        "Sorry, your application has been rejected. Please ensure to attach the proof of attendance and re-apply for further approval.",
        " Sorry, your application has been rejected. The total allowable CPD hours and Activities has already been granted to the user."
    ],
    KIV: [
        "Please be informed that, at the moment, the activities you submitted are not considered as CPD Hours. However, we will keep them in view (KIV) for further deliberation."
    ]
};

function StatusBatchUpdater({ closePopup, handleSubmit, loading, currentCPDStatus }) {
    const location = useLocation();
    const initialValues = {
        status: null,
        reason: "",
        cpd_hours: "",
        decision_committee_date: "",
    };

    const validationSchema = Yup.object().shape({
        status: Yup.string().required("Status is required"),
        reason: Yup.string().when("status", {
            is: (val) => ["REJECTED", "KIV"].includes(val),
            then: () => Yup.string().required("Reason is required"),
        }),
        cpd_hours: Yup.number().when("status", {
            is: "APPROVED",
            then: () => Yup.number().required("CPD Hours Granted is required"),
        }),
        decision_committee_date: Yup.date().nullable().when('status', {
            is: "PENDING",
            then: () => Yup.date().required('Committee decision date is required')
        })
    });

    // Determine options based on currentCPDStatus
    const getStatusOptions = () => {
        switch (currentCPDStatus) {
            case 1:
                return [{ value: "SUBMITTED", label: "SUBMIT" }];
            case 2:
                return [
                    { value: "PENDING", label: "PENDING" },
                    { value: "APPROVED", label: "APPROVE" },
                    { value: "REJECTED", label: "REJECT" },
                    { value: "KIV", label: "KIV" }
                ];
            case 5:
                return [
                    { value: "APPROVED", label: "APPROVE" },
                    { value: "REJECTED", label: "REJECT" }
                ];
            default:
                return []; // Handle any other status as needed
        }
    };

    const statusOptions = getStatusOptions();

    return (
        <div>
            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize={true}>
                {({ setFieldValue, values, errors }) => (
                    <Form className="stepper-form-container">
                        {/* Status Field */}
                        <FormInputLayout label="Status" formikName="status" required={false}>
                            <Select
                                options={statusOptions}
                                styles={colourStylesFullWidth}
                                onChange={(selectedOption) => setFieldValue("status", selectedOption.value)}
                                value={statusOptions.find(option => option.value === values.status)}
                                isSearchable={false}
                            />
                            <ErrorMessage name="status" component="div" className="text-danger fs-14 fw-400 mt-1" />
                        </FormInputLayout>

                        {/* Conditional Fields */}
                        {values.status === "APPROVED" && (
                            <FormInputLayout label="CPD Hours Granted" formikName="cpd_hours" required={false}>
                                <Field name="cpd_hours" type="number" className="form-control" />
                                <ErrorMessage name="cpd_hours" component="div" className="text-danger fs-14 fw-400 mt-1" />
                            </FormInputLayout>
                        )}

                        {/* Committee Decision Date Field */}
                        {values.status === "PENDING" && (
                            <FormInputLayout label="Committee Decision Date" formikName="decision_committee_date">
                                <DatePicker
                                    value={
                                        values.decision_committee_date &&
                                        moment(values.decision_committee_date).isValid()
                                            ? moment(values.decision_committee_date).toDate()
                                            : undefined
                                    }
                                    onChange={(date) => {
                                        const formattedDate = new Date(date).toISOString();
                                        setFieldValue("decision_committee_date", formattedDate);
                                    }}
                                    dateFormat="DD/MM/YYYY"
                                    months={["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]}
                                />
                                <ErrorMessage
                                    name="decision_committee_date"
                                    component="div"
                                    className="text-danger fs-14 fw-400 mt-1"
                                />
                            </FormInputLayout>
                        )}

                        {/* Reason Field */}
                        {values.status === "PENDING" ? (
                            <FormInputLayout label="Reason" formikName="reason" required={false}>
                                <Field name="reason" type="text" className="form-control" />
                                <ErrorMessage name="reason" component="div" className="text-danger fs-14 fw-400 mt-1" />
                            </FormInputLayout>
                        ) : (values.status === "REJECTED" || values.status === "KIV") && (
                            <FormInputLayout label="Reason" formikName="reason" required={false}>
                                <Select
                                    options={rejectionReasons[values.status]?.map(reason => ({ value: reason, label: reason }))}
                                    onChange={(selectedOption) => setFieldValue("reason", selectedOption.value)}
                                    value={rejectionReasons[values.status]?.find(reason => reason.value === values.reason)}
                                    styles={colourStylesFullWidth}
                                />
                                <ErrorMessage name="reason" component="div" className="text-danger fs-14 fw-400 mt-1" />
                            </FormInputLayout>
                        )}

                        <div className="border-before pt-4"></div>
                        <div className="d-flex justify-content-end gap-4">
                            <div className="line-button d-flex gap-5 justify-content-end pt-5 btn_css">
                                <button className="cancel-btn" onClick={closePopup}>
                                    Cancel
                                </button>
                            </div>
                            <div className="right-content d-flex gap-5 justify-content-end pt-5 btn_css">
                                <PlainButton type="" isLoading={loading}>
                                    Confirm
                                </PlainButton>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
}

export default StatusBatchUpdater;
